/**
 * Sentry para la web (React).
 *
 * Gateado por REACT_APP_SENTRY_DSN: si no está definido, todas las funciones son no-op
 * y el SDK ni siquiera se descarga. El SDK se importa de forma diferida vía ./sentrySdk.js
 * (solo errores, sin tracing/replay) para no sumar nada al bundle inicial
 * (meta del plan: main.js < 500 KB). La performance la mide web-vitals, no Sentry.
 *
 * Contexto que se adjunta a cada evento: id de usuario (uid), rol, idc y typecompany.
 * Nunca email ni nombre (sendDefaultPii: false). Ver docs/manual-observabilidad.md.
 *
 * Uso:
 *   initSentry()                 → una vez, en src/index.js
 *   setSentryUser(profile)       → desde AuthContext cuando cambia el usuario
 *   captureException(err, extra) → en catch de pantallas o servicios V2
 */
const DSN = process.env.REACT_APP_SENTRY_DSN;
const isPrerender = typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent);
const enabled = Boolean(DSN) && !isPrerender;

let sdk = null;
let ready = null;
// Cola para eventos que ocurran antes de que el SDK termine de cargar.
const pending = [];

const flush = () => {
  while (pending.length) {
    const fn = pending.shift();
    try {
      fn(sdk);
    } catch (e) {
      // Nunca romper la app por telemetría.
    }
  }
};

const withSdk = (fn) => {
  if (!enabled) return;
  if (sdk) {
    fn(sdk);
    return;
  }
  pending.push(fn);
};

export const initSentry = () => {
  if (!enabled || ready) return ready;
  ready = import("./sentrySdk")
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
        release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
        sendDefaultPii: false,
        ignoreErrors: [
          // Ruido típico de extensiones y de red que no corresponde al código de goshofi.
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          /^Loading chunk \d+ failed/,
          /^Loading CSS chunk \d+ failed/,
          "Network Error",
        ],
      });
      sdk = Sentry;
      flush();
      return Sentry;
    })
    .catch(() => {
      // Si el SDK no carga (bloqueador, red), la app sigue igual.
      pending.length = 0;
      return null;
    });
  return ready;
};

/**
 * Adjunta el usuario logueado como contexto. Acepta el `profile` de AuthContext.
 * Con `null` o un perfil con `logged: false` limpia el contexto (logout).
 */
export const setSentryUser = (profile) => {
  withSdk((Sentry) => {
    if (!profile || !profile.logged) {
      Sentry.setUser(null);
      Sentry.setTags({ rol: undefined, idc: undefined, typecompany: undefined });
      return;
    }
    Sentry.setUser({ id: profile.id || profile.uid || undefined });
    Sentry.setTags({
      rol: profile.rol || "sin-rol",
      idc: profile.idc || "sin-idc",
      typecompany: profile.typecompany || undefined,
    });
  });
};

/** Captura una excepción con contexto extra. Sin DSN, hace console.error. */
export const captureException = (error, extra) => {
  if (!enabled) {
    if (process.env.NODE_ENV !== "production") console.error(error, extra);
    return;
  }
  withSdk((Sentry) => Sentry.captureException(error, extra ? { extra } : undefined));
};

/** Mensaje informativo (por ejemplo, un estado inesperado que no lanza error). */
export const captureMessage = (message, extra) => {
  withSdk((Sentry) => Sentry.captureMessage(message, extra ? { extra } : undefined));
};
